import React, { FC, useContext, useState } from 'react';
import { format } from 'date-fns';
import TableWithColumnSelector, { OnClickFn, TableType } from '../../core/TableWithColumnSelector';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import { useQuery } from '@apollo/client';
import {
  anesthesiologistUtilizationAnalyticsQuery,
  anesthesiologistUtilizationAnalyticsTotalsQuery,
} from '../../../graph/dashboard';
import {
  AnesthesiologistUtilizationTableData,
  AnesthesiologistUtilizationTableTotalsData,
} from '../../../types/Analytics';
import { getMinutes } from './util';
import { TimelineColumnLegend } from './components/ColumnLegend';
import { Button, CircularProgress } from '@material-ui/core';
import { ArrowBack } from '@material-ui/icons';
import { AggregationContext } from './Aggregation';
import { pickFilter } from './GeneralAnalytics';
import AnesthesiologistPatientsAnalytics from './AnesthesiologistPatientsAnalytics';

const toTableData = (
  data: AnesthesiologistUtilizationTableData[],
  totals: AnesthesiologistUtilizationTableTotalsData,
  aggregation: 'average' | 'median',
  onClick?: OnClickFn
): TableType => {
  const aggregationTitle = aggregation === 'average' ? 'Avg.' : 'Med.';

  const baseHeaders = [
    'Anesthesiologist',
    'Procedures',
    'Surgery Days',
    `Patient In to Anesthesia Start ${aggregationTitle}`,
    `Anesthesia Start to Anesthesia Complete ${aggregationTitle}`,
  ];

  const total = [
    totals?.anesthesiologist || 'Total',
    totals?.numberOfProcedures?.toString() || '-',
    totals?.surgeryDays?.toString() || '-',
    getMinutes(
      aggregation === 'average' ? totals?.patientInToAnesthesiaStartAvg : totals?.patientInToAnesthesiaStartMed
    ),
    getMinutes(
      aggregation === 'average'
        ? totals?.anesthesiaStartToAnesthesiaCompleteAvg
        : totals?.anesthesiaStartToAnesthesiaCompleteMed
    ),
  ];

  const totalRow = {
    id: 'total',
    columns: total,
  };

  const getSortableLastName = (fullName: string) => {
    if (!fullName) return '';

    const suffixes = ['Jr', 'Sr', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];

    const parts = fullName.trim().split(/\s+/);

    if (parts.length > 1 && suffixes.includes(parts[parts.length - 1].replace(/[.,]/g, ''))) {
      return parts[parts.length - 2];
    }
    return parts[parts.length - 1];
  };

  const sortedData = [...(data || [])].sort((a, b) => {
    const lastNameA = getSortableLastName(a.anesthesiologist);
    const lastNameB = getSortableLastName(b.anesthesiologist);
    return lastNameA.localeCompare(lastNameB);
  });

  return {
    configHeader: [...baseHeaders],
    headers: [
      {
        id: 'h1',
        columns: [
          'Anesthesiologist',
          'Procedures',
          'Surgery Days',
          {
            text: `Patient In to Anesthesia Start ${aggregationTitle}`,
            InfoComponent: () => (
              <TimelineColumnLegend
                title="Patient In to Anesthesia Start"
                description="Time elapsed from when the patient enters the OR until anesthesia start."
              />
            ),
          },
          {
            text: `Anesthesia Start to Anesthesia Complete ${aggregationTitle}`,
            InfoComponent: () => (
              <TimelineColumnLegend
                title="Anesthesia Start to Anesthesia Complete"
                description="Time elapsed from anesthesia start until anesthesia is complete."
              />
            ),
          },
        ],
      },
    ],
    rows: sortedData
      .map(e => ({
        id: e?.id.toString(),
        columns: [
          e.anesthesiologist,
          e.numberOfProcedures.toString(),
          e.surgeryDays.toString(),
          getMinutes(aggregation === 'average' ? e.patientInToAnesthesiaStartAvg : e.patientInToAnesthesiaStartMed),
          getMinutes(
            aggregation === 'average'
              ? e.anesthesiaStartToAnesthesiaCompleteAvg
              : e.anesthesiaStartToAnesthesiaCompleteMed
          ),
        ],
        onClick,
      }))
      .concat([totalRow]),
  };
};

const AnesthesiologistUtilizationAnalytics: FC<{ filter: any; setFilter: any }> = ({ filter }) => {
  const aggregation = useContext(AggregationContext);
  const dateRangeJSON = filter?.dateRange?.toJSON();
  const dateRange = { from: format(dateRangeJSON.from, 'YYYY-MM-DD'), to: format(dateRangeJSON.to, 'YYYY-MM-DD') };
  const { operationRooms, physicians, ...rest } = filter || {};

  const [anesthesiologistId, setAnesthesiologistId] = useState<{
    idNumber: number;
    idString: string;
  } | null>(null);

  const sanitizedOperationRoomsFilter =
    Array.isArray(operationRooms) && operationRooms.length > 0
      ? { operationRooms: operationRooms.map(or => parseInt(or, 10)) }
      : isFinite(parseInt(operationRooms, 10))
        ? { operationRooms: [parseInt(operationRooms, 10)] }
        : {};

  const sanitizedPhysiciansFilter =
    Array.isArray(physicians) && physicians.length > 0
      ? { physicians: physicians.map(py => parseInt(py, 10)) }
      : isFinite(parseInt(physicians, 10))
        ? { physicians: [parseInt(physicians, 10)] }
        : {};

  const { data, loading } = useQuery(anesthesiologistUtilizationAnalyticsQuery, {
    variables: {
      filter: {
        dateRange: dateRangeJSON,
        ...sanitizedOperationRoomsFilter,
        ...sanitizedPhysiciansFilter,
        ...pickFilter(rest),
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  const { data: totalsData, loading: totalsLoading } = useQuery(anesthesiologistUtilizationAnalyticsTotalsQuery, {
    variables: {
      filter: {
        dateRange: dateRangeJSON,
        ...sanitizedOperationRoomsFilter,
        ...sanitizedPhysiciansFilter,
        ...pickFilter(rest),
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  const onAnesthesiologistClick = (anesthesiologistId: string) => {
    const id = anesthesiologistId?.split('#')?.[0];
    setAnesthesiologistId({ idNumber: Number(id), idString: anesthesiologistId });
  };

  const onBack = (_: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setAnesthesiologistId(null);
  };

  const anesthesiologistUtilizationAnalyticsTotals = totalsData?.anesthesiologistUtilizationAnalyticsTotals;

  const table: TableType = toTableData(
    data?.anesthesiologistUtilizationAnalytics || [],
    anesthesiologistUtilizationAnalyticsTotals || {},
    aggregation,
    onAnesthesiologistClick
  );

  if (anesthesiologistId) {
    const anesthesiologistAnalytics = (data?.anesthesiologistUtilizationAnalytics || []).find(
      e => e?.id === anesthesiologistId.idString
    );

    return (
      <>
        <Button variant="contained" startIcon={<ArrowBack />} onClick={onBack} style={{ marginBottom: 8 }}>
          Back to Anesthesiologist Utilization Analytics
        </Button>
        <AnesthesiologistPatientsAnalytics
          anesthesiologistId={anesthesiologistId.idNumber}
          anesthesiologistName={anesthesiologistAnalytics?.anesthesiologist}
          dateRange={dateRange}
          filter={{
            dateRange: dateRangeJSON,
            ...sanitizedOperationRoomsFilter,
            ...sanitizedPhysiciansFilter,
            ...pickFilter(rest),
          }}
        />
      </>
    );
  }

  if (loading || totalsLoading) {
    return (
      <Box display="flex" alignItems="center" m={2} style={{ gap: '1em' }}>
        <CircularProgress size={30} />
        <Typography variant="body1" color="textSecondary">
          Loading…
        </Typography>
      </Box>
    );
  }

  return (
    <TableWithColumnSelector
      configName="anesthesiologist-table-config"
      tableId="Anesthesiologist-Analysis-table"
      tableName={`Analysis by Anesthesiologist`}
      excelFileName={`Anesthesiologist-Analysis-${format(dateRange.from, 'MM/DD/YYYY')}-${format(
        dateRange.to,
        'MM/DD/YYYY'
      )}`}
      {...table}
    />
  );
};

export default AnesthesiologistUtilizationAnalytics;
